import { useState, useEffect, CSSProperties } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { loadImage } from 'utils'
import useIsMounted from 'ismounted'

import Styles from './ImageWithProgressPercentage.module.scss'

interface Props extends React.ImgHTMLAttributes<any> {
  src: string
  loadingClassName?: string
  onError: () => void
  overridingStyle?: CSSProperties
  useOriginalSrc?: Boolean
}

export const ImageWithProgressPercentage = ({
  alt,
  src,
  loadingClassName,
  onError,
  overridingStyle,
  useOriginalSrc = false,
  ...imgElementProps
}: Props) => {
  const [progress, setProgress] = useState(0)
  const [imageSrc, setImageSrc] = useState('')
  const isMounted = useIsMounted()

  useEffect(() => {
    if (useOriginalSrc) {
      setImageSrc(src)
      return
    }

    const fetchImage = async () => {
      try {
        const imageObjectURL = await loadImage(src, setProgress)
        isMounted.current && setImageSrc(imageObjectURL as string)
      } catch (err) {
        onError()
      }
    }
    !imageSrc.length && fetchImage()

    return () => URL.revokeObjectURL(imageSrc)
  }, [src, onError, imageSrc, isMounted, useOriginalSrc])

  return (
    <>
      {imageSrc.length ? (
        <img data-testid="image-container" src={imageSrc} alt={alt} {...imgElementProps} />
      ) : (
        <div
          className={cx(Styles.progress, loadingClassName)}
          style={overridingStyle}
          role="progressbar"
          aria-busy="true"
          aria-live="polite"
          data-testid="image-load-container"
        >
          {progress < 0 ? 'Loading...' : `${progress}%`}
        </div>
      )}
    </>
  )
}
